import type { McpServer } from '../integrations/mcpServerRegistry';
import type { ConnectedAccount } from '../integrations/StripePayments';

export type IntentKey = 'services' | 'agent';
export type SurfaceKey = 'website' | 'product' | 'email' | 'gateway';
/** `pending` = services intent, decision not made yet (the Get paid step comes after submit). */
export type PaymentsChoice = 'stripe' | 'later' | 'pending' | 'not_applicable';

/**
 * Build-screen status of the onboarding job's setup wake. `waiting` is
 * derived on the client from the live stream (the job paused on a question
 * for the owner); the server summary only reports the other five.
 */
export type RunStatus = 'queued' | 'running' | 'waiting' | 'completed' | 'failed' | 'cancelled';

export interface RunProgressItem {
  key: string;
  label: string;
  state: 'done' | 'active' | 'todo';
  detail: string | null;
}

/** Mirrors organization_onboarding_runs.summarize: the setup wake of the onboarding job. */
export interface RunSummary {
  /** The wake (agent run) id; null before the first wake is queued. */
  id: string | null;
  job_id: string | null;
  status: RunStatus;
  headline: string | null;
  started_at: string | null;
  completed_at: string | null;
  error_message: string | null;
  progress: RunProgressItem[];
}

/** Mirrors GET /organization-onboarding. */
export interface OnboardingState {
  exists: boolean;
  required: boolean;
  building: boolean;
  onboarding_id?: string | null;
  status?: string | null;
  answers?: Record<string, unknown> | null;
  default_website_url?: string | null;
  intents: IntentKey[];
  mcp_server: McpServer | null;
  stripe: { account: ConnectedAccount | null; charge_capable: boolean } | null;
  runs: { job: RunSummary | null };
  /** The onboarding job (OWNED_JOBS_PLAN.md): what the build screen subscribes to. */
  job_id?: string | null;
}

export type StepKey = 'intent' | 'connect' | 'surfaces' | 'payments' | 'website' | 'building';
export type StripeSubstep = 'choose' | 'handoff' | 'returned';

/** What survives a reload or the round trip to Stripe. */
export interface PersistedDraft {
  stepKey: StepKey;
  intents: IntentKey[];
  surfaces: SurfaceKey[];
  mcpServerId: string | null;
  payments: PaymentsChoice;
  stripeSubstep: StripeSubstep;
  websiteUrl: string;
  submissionId: string;
}

export const STRIPE_RETURN_PATH = '/?stripe=return&onboarding=1';

/**
 * Website (submit) comes before payments on purpose: the build job starts
 * the moment the website is confirmed, so Stripe onboarding and the build
 * overlap instead of queueing behind each other.
 */
export function stepsFor(intents: IntentKey[]): StepKey[] {
  const steps: StepKey[] = ['intent', 'connect'];
  if (intents.includes('agent')) steps.push('surfaces');
  steps.push('website');
  if (intents.includes('services')) steps.push('payments');
  steps.push('building');
  return steps;
}

export function draftStorageKey(onboardingId: string) {
  return `aimdoc-onboarding:${onboardingId}`;
}

export function readDraft(onboardingId: string): PersistedDraft | null {
  try {
    const raw = localStorage.getItem(draftStorageKey(onboardingId));
    return raw ? (JSON.parse(raw) as PersistedDraft) : null;
  } catch {
    return null;
  }
}

export function writeDraft(onboardingId: string, draft: PersistedDraft) {
  try {
    localStorage.setItem(draftStorageKey(onboardingId), JSON.stringify(draft));
  } catch {
    // Storage can be unavailable (private mode); the flow still works, it just will not resume.
  }
}

export function clearDraft(onboardingId: string) {
  try {
    localStorage.removeItem(draftStorageKey(onboardingId));
  } catch {
    // ignore
  }
}


/**
 * A display name for the product behind an MCP URL, so we never ask for one:
 * `https://mcp.acme.com/mcp` -> "Acme", `https://api.aimdoc.ai/mcp` -> "Aimdoc".
 * Strips infrastructure subdomains and the public suffix; keeps the brand label.
 */
export function productNameFromUrl(url: string): string {
  const raw = url.trim();
  if (!raw) return '';
  let host = '';
  try {
    host = new URL(raw.includes('://') ? raw : `https://${raw}`).hostname.toLowerCase();
  } catch {
    return '';
  }
  const labels = host.split('.').filter(Boolean);
  if (labels.length < 2) return '';
  // drop the public suffix (last label, or last two for co.uk-style suffixes)
  const suffixLen = labels.length >= 3 && labels[labels.length - 1].length === 2 && labels[labels.length - 2].length <= 3 ? 2 : 1;
  const domainLabels = labels.slice(0, labels.length - suffixLen);
  const infra = new Set(['mcp', 'api', 'www', 'app', 'dev', 'staging', 'prod', 'services', 'agent', 'agents']);
  const candidates = domainLabels.filter((label) => !infra.has(label));
  const brand = (candidates.length ? candidates : domainLabels)[candidates.length ? candidates.length - 1 : domainLabels.length - 1];
  if (!brand) return '';
  return brand.charAt(0).toUpperCase() + brand.slice(1);
}
